import FileIcon from "@/modules/common/components/ui-kit/icons/file-icon";
import MoreActionsMenu from "@/modules/common/components/ui-kit/menus/more-actions-menu";
import { FinancialProposal } from "@/modules/property-tracks/types";
import { Box, Typography } from "@mui/material";
import { format, differenceInCalendarDays } from "date-fns";
import DownloadProposal from "./actions/download";
import PreviewProposal from "./actions/preview-proposal";
import EditFinancialProposal from "./actions/edit";
import { useParams } from "react-router-dom";
import { useTrackQuery } from "@/modules/property-tracks/hooks/track.hook";
import OpenInWhatsapp from "./actions/whatsapp";
import ExtendFinancialProposal from "./actions/extend";

interface ProposalItemProps {
  proposal: FinancialProposal;
  customerUId: string;
}
export default function ProposalItem({
  proposal,
  customerUId,
}: ProposalItemProps) {
  const { trackId } = useParams<{ trackId: string }>();

  const { data: track } = useTrackQuery({
    uid: trackId,
  });

  const getExpirationColor = (expiresAt?: Date) => {
    if (!expiresAt) return undefined;
    const daysLeft = differenceInCalendarDays(new Date(expiresAt), new Date());
    if (daysLeft < 0) return "error"; // red - expired
    if (daysLeft <= 2) return "warning"; // yellow - 2 days left
    return "textSecondary"; // default color
  };

  return (
    <Box
      sx={(t) => ({
        display: "flex",
        p: t.nawy.spacing.sm,
        gap: t.nawy.spacing.sm,
        border: `1px solid ${t.nawy.colors.semantic.border.primary}`,
        borderRadius: t.nawy.radius.lg,
      })}
    >
      <FileIcon fileType="application/pdf" />
      <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
        <Typography
          sx={(t) => t.nawy.typography.english.variants.body.md.regular}
        >
          {proposal.fileName}
        </Typography>
        {proposal.expiresAt && (
          <Typography
            color={getExpirationColor(proposal.expiresAt)}
            sx={(t) => t.nawy.typography.english.variants.body.sm.regular}
          >
            Expires: {format(new Date(proposal.expiresAt), "dd MMM yyyy")}
          </Typography>
        )}
      </Box>
      <MoreActionsMenu>
        <PreviewProposal fileUrl={proposal.fileUrl} />
        <DownloadProposal fileUrl={proposal.fileUrl} />
        <ExtendFinancialProposal
          uid={proposal.uid}
          rootVersion={proposal.rootVersion}
          minorVersion={proposal.minorVersion}
        />
        <OpenInWhatsapp
          customerUId={customerUId}
          trackUid={trackId}
          minorVersion={proposal.minorVersion}
          rootVersion={proposal.rootVersion}
        />
        <EditFinancialProposal
          uid={proposal.uid}
          defaultValues={{
            compoundName: track?.property.compound?.name ?? "",
            customerName: track?.owner?.name ?? "",
            propertyTypeName: track?.property.propertyType?.name ?? "",
            unitArea: track?.property.unitArea ?? 0,
            scopeOfFinishing: track?.scopeOfFinishing,
          }}
        />
      </MoreActionsMenu>
    </Box>
  );
}
